import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles, RefreshCw, Gauge, Route, Clock, TrendingUp, TrendingDown, Moon, Activity } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const t = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-tenant-button border border-tenant-border bg-tenant-bg p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <p className={`mt-1 text-lg font-bold tracking-tight ${accent || ""}`}>{value}</p>
    </div>
  );
}

export default function VehicleHistoryPanel({ vehicleId }) {
  const [date, setDate] = useState(today());
  const [hist, setHist] = useState(null);
  const [summary, setSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const loadHistory = useCallback(() => {
    setHist(null);
    api.get(`/operator/analytics/vehicles/${vehicleId}/history?date=${date}`).then(({ data }) => setHist(data));
  }, [vehicleId, date]);

  const loadSummary = useCallback((refresh = false) => {
    setAiLoading(true);
    api.get(`/operator/analytics/vehicles/${vehicleId}/summary?date=${date}${refresh ? "&refresh=true" : ""}`)
      .then(({ data }) => setSummary(data)).finally(() => setAiLoading(false));
  }, [vehicleId, date]);

  useEffect(() => { loadHistory(); loadSummary(false); }, [loadHistory, loadSummary]);

  const s = hist?.summary;

  return (
    <div data-testid="vehicle-history-panel" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">Driving history & pattern</p>
        <Input data-testid="history-date" type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="h-9 w-auto" />
      </div>

      {/* AI narrative */}
      <div className="rounded-tenant-card border border-tenant-primary/30 bg-tenant-primary/5 p-4">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-tenant-primary"><Sparkles className="h-4 w-4" /> AI driving summary</p>
          <Button data-testid="history-refresh-ai" variant="ghost" size="sm" onClick={() => loadSummary(true)} disabled={aiLoading} className="h-7 gap-1 text-xs">
            {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate
          </Button>
        </div>
        <p data-testid="history-ai-narrative" className="mt-2 text-sm leading-relaxed text-foreground">
          {aiLoading && !summary ? "Analysing driving pattern…"
            : summary?.narrative || "No travel recorded for this day yet — the AI summary appears once the vehicle logs movement."}
        </p>
      </div>

      {/* day metrics */}
      {!hist ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <Metric icon={Route} label="Distance" value={`${s.distance_km} km`} />
            <Metric icon={Clock} label="Moving time" value={`${s.moving_minutes}m`} />
            <Metric icon={Gauge} label="Max speed" value={`${s.max_speed} km/h`} accent={s.max_speed > 60 ? "text-status-recovery" : ""} />
            <Metric icon={Activity} label="Avg speed" value={`${s.avg_moving_speed} km/h`} />
            <Metric icon={TrendingUp} label="Harsh accel" value={s.harsh_accel} accent={s.harsh_accel ? "text-status-overdue" : ""} />
            <Metric icon={TrendingDown} label="Harsh brake" value={s.harsh_brake} accent={s.harsh_brake ? "text-status-overdue" : ""} />
            <Metric icon={Moon} label="Night riding" value={`${s.night_pct}%`} accent={s.night_pct > 30 ? "text-status-overdue" : ""} />
            <Metric icon={Activity} label="Active windows" value={s.active_windows} />
          </div>

          {/* 5-min timeline */}
          <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft">
            <p className="mb-3 text-sm font-semibold">5-minute travel log {s.riders.length > 0 && <span className="font-normal text-muted-foreground">· {s.riders.join(", ")}</span>}</p>
            {hist.logs.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No movement logged for this day.</p>
            ) : (
              <div className="max-h-80 space-y-1.5 overflow-y-auto">
                {hist.logs.map((l) => (
                  <div key={l.id} data-testid={`history-window-${l.id}`} className="flex items-center justify-between rounded-tenant-button border border-tenant-border px-3 py-2 text-sm">
                    <div>
                      <span className="font-medium">{t(l.window_start)} → {t(l.window_end)}</span>
                      {l.rider_name && <span className="ml-2 text-xs text-muted-foreground">{l.rider_name}</span>}
                      {l.night && <Moon className="ml-1.5 inline h-3 w-3 text-muted-foreground" />}
                    </div>
                    <div className="flex items-center gap-2.5 text-xs">
                      <span className="font-medium">{l.distance_km} km</span>
                      <span className="text-muted-foreground">max {l.max_speed} km/h</span>
                      {(l.harsh_accel > 0 || l.harsh_brake > 0) && (
                        <span className="rounded-full bg-status-overdue/15 px-2 py-0.5 font-semibold text-status-overdue">{l.harsh_accel + l.harsh_brake} harsh</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
